import { logger } from "./logger";
import { DEFAULT_LOCALE } from "./constants";

const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "";
const adminHeaders = { "Content-Type": "application/json", "X-Admin-Token": ADMIN_TOKEN };

export async function adminFetch(table, { method = "GET", body, params } = {}) {
  const t0 = performance.now();
  let url = `/api/admin/${table}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += "?" + qs;
  }
  if (_currentUserUid) {
    if (method === "POST" && !body?.created_by_uid) { if (!body) body = {}; body.created_by_uid = _currentUserUid; }
    if (method === "PATCH" && !body?.edited_by_uid) { if (!body) body = {}; body.edited_by_uid = _currentUserUid; }
  }
  const opts = { method, headers: adminHeaders };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  const elapsed = Math.round(performance.now() - t0);
  if (elapsed > 1000) {
    logger.warn(`[API] ${method} /api/admin/${table} → ${elapsed}ms (slow)`);
  } else {
    logger.info(`[API] ${method} /api/admin/${table} → ${elapsed}ms`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || "Erro na requisição");
  }
  return res.json();
}

export function whatsappUrl(number, message) {
  const digits = number.replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export async function sendNotificationEmail(to, subject, html, replyTo, replyName, fromAddress, fromName) {
  if (!to) return;
  const response = await fetch("/api/email", {
    method: "POST",
    headers: adminHeaders,
    body: JSON.stringify({ to, subject, html, replyTo, replyName, fromAddress, fromName }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    logger.error("[sendNotificationEmail] Server error:", response.status, errorText);
  }
}

// ---- LEADS ----
export async function createLead({ name, email, phone, service, message, contactMethod, nationality }) {
  const res = await fetch("/api/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, phone, service, message, contactMethod, nationality }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao enviar pedido");
  }
  return res.json();
}

export async function getLeads() {
  return adminFetch("leads", { params: { order: "created_at DESC" } });
}

export async function updateLeadStatus(uid, status) {
  return adminFetch("leads", { method: "PATCH", body: { uid, status } });
}

export async function deleteLead(uid) {
  return adminFetch("leads", { method: "DELETE", params: { uid } });
}

// ---- TESTIMONIALS ----
export async function getPublishedTestimonials(lang = DEFAULT_LOCALE) {
  return adminFetch("testimonials", { params: { locale: lang, order: "created_at DESC" } });
}

export async function getAllTestimonials(lang = DEFAULT_LOCALE) {
  return adminFetch("testimonials", { params: { locale: lang, order: "created_at DESC" } });
}

export async function createTestimonial(form, lang = DEFAULT_LOCALE) {
  return adminFetch("testimonials", { method: "POST", body: { ...form, locale: lang } });
}

export async function updateTestimonial(uid, form, lang = DEFAULT_LOCALE) {
  return adminFetch("testimonials", { method: "PATCH", body: { uid, ...form, locale: lang } });
}

export async function deleteTestimonial(uid) {
  return adminFetch("testimonials", { method: "DELETE", params: { uid } });
}

// ---- PORTFOLIO ----
const PORTFOLIO_FIELDS = ["title","category","description","image_url","featured","published"];
export async function getPortfolio(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const p = ui?.portfolio || {};
  const allUids = [...new Set(Object.keys(p).filter(k => k.endsWith("_title")).map(k => k.replace("_title","")))];
  if (allUids.length === 0) return [];
  return allUids.sort().map(uid => {
    const item = { uid };
    PORTFOLIO_FIELDS.forEach(f => {
      const v = p[`${uid}_${f}`];
      if (f === "featured" || f === "published") item[f] = (v === "1" || v === true) ? 1 : 0;
      else item[f] = v || null;
    });
    return item;
  }).filter(item => item.title).sort((a, b) => (a.uid || "").localeCompare(b.uid || ""));
}
export async function createPortfolioItem(form, lang = DEFAULT_LOCALE) {
  const existing = await getPortfolio(lang);
  const uid = crypto.randomUUID().replace(/-/g, "");
  const values = {};
  PORTFOLIO_FIELDS.forEach(f => {
    let v = form[f];
    if (f === "featured" || f === "published") v = v ? "1" : "0";
    values[`${uid}_${f}`] = v !== undefined && v !== null ? String(v) : "";
  });
  await updateUiStrings("portfolio", values, lang);
  return { uid, ...form };
}
export async function updatePortfolioItem(uid, form, lang = DEFAULT_LOCALE) {
  const values = {};
  PORTFOLIO_FIELDS.filter(f => form[f] !== undefined).forEach(f => {
    let v = form[f];
    if (f === "featured" || f === "published") v = v ? "1" : "0";
    values[`${uid}_${f}`] = v !== undefined && v !== null ? String(v) : "";
  });
  if (Object.keys(values).length > 0) await updateUiStrings("portfolio", values, lang);
}
export async function deletePortfolioItem(uid, lang = DEFAULT_LOCALE) {
  const values = {};
  PORTFOLIO_FIELDS.forEach(f => { values[`${uid}_${f}`] = ""; });
  await updateUiStrings("portfolio", values, lang);
}

// ---- EMAIL PROCESSES ----
export async function getEmailProcesses() {
  const processes = await adminFetch("email_processes", { params: { order: "created_at ASC" } });
  return processes || [];
}

export async function getEmailProcess(key) {
  const processes = await adminFetch("email_processes");
  const proc = (processes || []).find(p => p.process_key === key);
  return proc || null;
}

export async function updateEmailProcess(uid, settings) {
  return adminFetch("email_processes", { method: "PATCH", body: { uid, ...settings } });
}

export async function createEmailProcess({ process_key, label, enabled, email_subject, email_from_name, email_from_address, email_template }) {
  return adminFetch("email_processes", {
    method: "POST",
    body: { process_key, label, enabled: enabled ? 1 : 0, email_subject, email_from_name, email_from_address, email_template },
  });
}

export async function deleteEmailProcess(uid) {
  return adminFetch("email_processes", { method: "DELETE", params: { uid } });
}

// ---- EMAIL RECIPIENTS ----
export async function getEmailRecipients(processId) {
  const rows = await adminFetch("email_recipients", { params: { order: "created_at ASC" } });
  return (rows || []).filter(r => String(r.process_id) === String(processId));
}

export async function addEmailRecipient({ process_id, email, label, enabled }) {
  return adminFetch("email_recipients", {
    method: "POST",
    body: { process_id, email, label: label || "", enabled: enabled !== false ? 1 : 0 },
  });
}

export async function updateEmailRecipient(uid, { email, label, enabled }) {
  return adminFetch("email_recipients", {
    method: "PATCH",
    body: { uid, email, label, enabled: enabled ? 1 : 0 },
  });
}

export async function deleteEmailRecipient(uid) {
  return adminFetch("email_recipients", { method: "DELETE", params: { uid } });
}

// ---- CONTACT METHODS ----
export async function getContactMethods(lang = DEFAULT_LOCALE) {
  try {
    const rows = await adminFetch("contact_methods", { params: { locale: lang, order: "order_index ASC" } });
    return (rows || []).map(r => r.name);
  } catch {
    logger.warn("[API] getContactMethods: failed");
    return [];
  }
}

export async function updateContactMethods(names, lang = DEFAULT_LOCALE) {
  const existing = await adminFetch("contact_methods", { params: { locale: lang } });
  const activeExisting = (existing || []).filter(r => r.active !== 0 && !r.deleted_at);
  const existingNames = activeExisting.map(r => r.name);

  // PATCH existing items that changed order or stayed the same
  for (let i = 0; i < names.length; i++) {
    const old = activeExisting[i];
    if (old) {
      if (old.name !== names[i] || parseInt(old.order_index) !== i) {
        await adminFetch("contact_methods", { method: "PATCH", body: { uid: old.uid, name: names[i], order_index: i } });
      }
    } else {
      await adminFetch("contact_methods", { method: "POST", body: { name: names[i], order_index: i, locale: lang } });
    }
  }
  for (let i = names.length; i < activeExisting.length; i++) {
    await adminFetch("contact_methods", { method: "PATCH", body: { uid: activeExisting[i].uid, active: 0, deleted_at: new Date().toISOString() } });
  }
}

// ---- SETTINGS ----
let _settingsUidCache = {};

export async function getSettings() {
  const rows = await adminFetch("settings");
  (rows || []).forEach(r => { _settingsUidCache[r.key] = r.uid; });
  return (rows || []).reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
}

export async function updateSetting(key, value) {
  const uid = _settingsUidCache[key];
  if (uid) {
    return adminFetch("settings", { method: "PATCH", body: { uid, value } });
  }
  const rows = await adminFetch("settings");
  const existing = (rows || []).find(r => r.key === key);
  if (existing) {
    _settingsUidCache[key] = existing.uid;
    return adminFetch("settings", { method: "PATCH", body: { uid: existing.uid, value } });
  }
  const created = await adminFetch("settings", { method: "POST", body: { key, value } });
  if (created?.uid) _settingsUidCache[key] = created.uid;
  return created;
}

// ---- SERVICES ----
export async function getServices(lang = DEFAULT_LOCALE) {
  const data = await adminFetch("services", { params: { locale: lang, order: "order_index ASC" } });
  return (data || []).filter(s => !s.deleted_at).map(s => ({
    uid: s.uid,
    title: s.title || "",
    description: s.description || "",
    icon_name: s.icon_name || "",
    image_url: s.image_url || "",
    order_index: parseInt(s.order_index) || 0,
    published: s.published === 1 || s.published === true ? 1 : 0,
    bullets: s.bullets ? (typeof s.bullets === "string" ? JSON.parse(s.bullets) : s.bullets) : [],
  }));
}

export async function createService(form, lang = DEFAULT_LOCALE) {
  const payload = { ...form, locale: lang };
  if (Array.isArray(payload.bullets)) payload.bullets = JSON.stringify(payload.bullets);
  if (payload.published !== undefined) payload.published = payload.published ? 1 : 0;
  return adminFetch("services", { method: "POST", body: payload });
}

export async function updateService(uid, updates, lang = DEFAULT_LOCALE) {
  const payload = { ...updates, uid, locale: lang };
  if (Array.isArray(payload.bullets)) payload.bullets = JSON.stringify(payload.bullets);
  if (payload.published !== undefined) payload.published = payload.published ? 1 : 0;
  return adminFetch("services", { method: "PATCH", body: payload });
}

export async function getServiceTexts(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const s = ui?.service || {};
  return {
    section_eyebrow: s.section_eyebrow || "",
    section_headline: s.section_headline || "",
    section_description: s.section_description || "",
    section_learn_more: s.section_learn_more || "",
  };
}
export async function updateServiceTexts(values, lang = DEFAULT_LOCALE) {
  await updateUiStrings("service", values, lang);
}

export async function deleteService(uid) {
  return adminFetch("services", { method: "PATCH", body: { uid, active: 0, deleted_at: new Date().toISOString() } });
}

// ---- SINGLE-ROW TABLES ----
async function getSingleRow(table, lang = DEFAULT_LOCALE) {
  const rows = await adminFetch(table, { params: { locale: lang } });
  return (rows || [])[0] || null;
}

let _uidCache = {};

export function clearUidCache(table) {
  if (table) _uidCache[table] = {};
  else _uidCache = {};
}

async function upsertRow(table, content, lang = DEFAULT_LOCALE) {
  if (!_uidCache[table]) _uidCache[table] = {};
  const cached = _uidCache[table][lang];
  if (cached) {
    await adminFetch(table, { method: "PATCH", body: { uid: cached, locale: lang, ...content } });
    return { success: true, uid: cached };
  }
  if (cached === null) {
    const created = await adminFetch(table, { method: "POST", body: { locale: lang, ...content } });
    if (created?.uid) _uidCache[table][lang] = created.uid;
    return created;
  }
  const rows = await adminFetch(table, { params: { locale: lang } });
  const existing = (rows || [])[0];
  if (existing) {
    _uidCache[table][lang] = existing.uid;
    await adminFetch(table, { method: "PATCH", body: { uid: existing.uid, locale: lang, ...content } });
    return { success: true, uid: existing.uid };
  }
  _uidCache[table][lang] = null;
  const created = await adminFetch(table, { method: "POST", body: { locale: lang, ...content } });
  if (created?.uid) _uidCache[table][lang] = created.uid;
  return created;
}

const HERO_KEYS = ["eyebrow","headline","subheadline","bg_image","img_alt"];

export async function getHero(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const h = ui?.hero || {};
  if (HERO_KEYS.some(k => h[k])) {
    const out = {};
    HERO_KEYS.forEach(k => { out[k] = h[k] || null; });
    return out;
  }
  return null;
}
export async function updateHero(content, lang = DEFAULT_LOCALE) {
  const values = {};
  HERO_KEYS.filter(k => content[k] !== undefined).forEach(k => { values[k] = content[k]; });
  if (Object.keys(values).length > 0) await updateUiStrings("hero", values, lang);
}

const ABOUT_FLAT_KEYS = ["eyebrow","headline","description_1","description_2","image","img_alt","since","whatsapp_message"];
export async function getAbout(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const a = ui?.about || {};
  const hasAny = ABOUT_FLAT_KEYS.some(k => a[k]) || Object.keys(a).some(k => k.startsWith("paragraph_") || k.startsWith("stat_") || k === "partner_logos");
  if (!hasAny) return null;
  const out = {};
  ABOUT_FLAT_KEYS.forEach(k => { if (a[k] !== undefined) out[k] = a[k]; });
  // Paragraphs: collect paragraph_0, paragraph_1, ...
  const pKeys = Object.keys(a).filter(k => k.startsWith("paragraph_")).sort((x, y) => parseInt(x.split("_")[1]) - parseInt(y.split("_")[1]));
  if (pKeys.length > 0) out.paragraphs = pKeys.map(k => a[k]);
  // Stats: collect stat_0_n, stat_0_label, stat_0_prefix, ...
  const sIdxs = [...new Set(Object.keys(a).filter(k => k.startsWith("stat_")).map(k => parseInt(k.split("_")[1])))].sort((a, b) => a - b);
  if (sIdxs.length > 0) out.stats = sIdxs.map(i => {
    const o = {};
    ["n", "label", "prefix"].forEach(f => { const v = a[`stat_${i}_${f}`]; if (v !== undefined) o[f] = v; });
    return o;
  });
  // Partner logos
  if (a.partner_logos) {
    try { out.partner_logos = JSON.parse(a.partner_logos); } catch { out.partner_logos = []; }
  }
  return out;
}
export async function updateAbout(content, lang = DEFAULT_LOCALE) {
  if (!content) return;
  const values = {};
  ABOUT_FLAT_KEYS.forEach(k => { if (content[k] !== undefined) values[k] = content[k]; });
  // Paragraphs: store as paragraph_0, paragraph_1, ...
  if (Array.isArray(content.paragraphs)) content.paragraphs.forEach((p, i) => { values[`paragraph_${i}`] = p; });
  // Stats: store as stat_0_n, stat_0_label, stat_0_prefix, ...
  if (Array.isArray(content.stats)) content.stats.forEach((s, i) => {
    ["n", "label", "prefix"].forEach(f => { if (s[f] !== undefined) values[`stat_${i}_${f}`] = s[f]; });
  });
  // Partner logos
  if (content.partner_logos !== undefined) {
    values.partner_logos = Array.isArray(content.partner_logos) ? JSON.stringify(content.partner_logos) : "";
  }
  if (Object.keys(values).length > 0) await updateUiStrings("about", values, lang);
}

const GLOBALS_KEYS = ["logo", "subtitle", "cta_label", "footer_description", "copyright", "contact_email", "locations", "whatsapp_number", "instagram_url"];

export async function getGlobals() {
  const s = await getSettings();
  const out = {};
  GLOBALS_KEYS.forEach(k => { out[k] = s[`globals_${k}`] || null; });
  // fallback para identity JSON (dados antigos)
  if (s.identity && GLOBALS_KEYS.every(k => !out[k])) {
    try { return { ...JSON.parse(s.identity), ...out }; } catch {}
  }
  return out;
}
export async function updateGlobals(content) {
  const ops = GLOBALS_KEYS.filter(k => content[k] !== undefined).map(k => updateSetting(`globals_${k}`, content[k]));
  await Promise.all(ops);
}

// ---- SECTION HEADERS ----
export async function getSectionHeaders(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const data = ui?.section_headers?.data;
  if (data) { try { return JSON.parse(data); } catch {} }
  return {};
}

export async function updateSectionHeader(section, content, lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const existing = ui?.section_headers?.data ? JSON.parse(ui.section_headers.data) : {};
  existing[section] = content;
  return updateUiStrings("section_headers", { data: JSON.stringify(existing) }, lang);
}

// ---- NAVIGATION (stored in ui_strings, one key per link) ----

let _navMigrated = {};

export async function getNavigation(lang = DEFAULT_LOCALE) {
  const rows = await adminFetch("navigation", { params: { locale: lang, order: "order_index ASC" } });
  return (rows || []).filter(r => r.active !== 0 && !r.deleted_at).map(r => ({ uid: r.uid, label: r.label, href: r.href }));
}

export async function updateNavigation(items, lang = DEFAULT_LOCALE) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.uid) {
      await adminFetch("navigation", { method: "PATCH", body: { uid: item.uid, label: item.label, href: item.href, order_index: i } });
    } else {
      const created = await adminFetch("navigation", { method: "POST", body: { locale: lang, label: item.label, href: item.href, order_index: i, active: 1 } });
      if (created?.uid) item.uid = created.uid;
    }
  }
  // Detect deletions (only if something was removed)
  const existing = await adminFetch("navigation", { params: { locale: lang } });
  const existingActive = (existing || []).filter(r => r.active !== 0 && !r.deleted_at);
  if (existingActive.length > items.length) {
    const currentUids = items.filter(i => i.uid).map(i => i.uid);
    for (const old of existingActive) {
      if (!currentUids.includes(old.uid)) {
        await adminFetch("navigation", { method: "PATCH", body: { uid: old.uid, active: 0, deleted_at: new Date().toISOString() } });
      }
    }
  }
}

// ---- FORM SERVICES ----
export async function getFormServices(lang = DEFAULT_LOCALE) {
  try {
    const rows = await adminFetch("form_services", { params: { locale: lang, order: "order_index ASC" } });
    return (rows || []).map(r => r.name);
  } catch {
    return [];
  }
}

export async function updateFormServices(names, lang = DEFAULT_LOCALE) {
  const existing = await adminFetch("form_services", { params: { locale: lang } });
  const active = (existing || []).filter(r => r.active !== 0 && !r.deleted_at);
  for (let i = 0; i < names.length; i++) {
    const old = active[i];
    if (old) {
      if (old.name !== names[i] || parseInt(old.order_index) !== i) {
        await adminFetch("form_services", { method: "PATCH", body: { uid: old.uid, name: names[i], order_index: i } });
      }
    } else {
      await adminFetch("form_services", { method: "POST", body: { name: names[i], order_index: i, locale: lang } });
    }
  }
  for (let i = names.length; i < active.length; i++) {
    await adminFetch("form_services", { method: "PATCH", body: { uid: active[i].uid, active: 0, deleted_at: new Date().toISOString() } });
  }
}

// ---- FORM PREFIXES ----
export async function getFormPrefixes() {
  try {
    return await adminFetch("form_prefixes", { params: { order: "order_index ASC" } });
  } catch {
    return [];
  }
}

export async function updateFormPrefixes(items) {
  const existing = await adminFetch("form_prefixes");
  const active = (existing || []).filter(r => r.active !== 0 && !r.deleted_at);
  for (let i = 0; i < items.length; i++) {
    const old = active[i];
    if (old) {
      const patch = { uid: old.uid, order_index: i };
      let changed = false;
      if (old.code !== items[i].code) { patch.code = items[i].code; changed = true; }
      if (old.country !== items[i].country) { patch.country = items[i].country; changed = true; }
      if (old.flag !== items[i].flag) { patch.flag = items[i].flag; changed = true; }
      if (changed || parseInt(old.order_index) !== i) { await adminFetch("form_prefixes", { method: "PATCH", body: patch }); }
    } else {
      await adminFetch("form_prefixes", { method: "POST", body: { code: items[i].code, country: items[i].country, flag: items[i].flag, order_index: i } });
    }
  }
  for (let i = items.length; i < active.length; i++) {
    await adminFetch("form_prefixes", { method: "PATCH", body: { uid: active[i].uid, active: 0, deleted_at: new Date().toISOString() } });
  }
}

// ---- FORM NATIONALITIES ----
export async function getFormNationalities(lang = DEFAULT_LOCALE) {
  try {
    const rows = await adminFetch("form_nationalities", { params: { locale: lang, order: "order_index ASC" } });
    return (rows || []).map(r => r.name);
  } catch {
    return [];
  }
}

export async function updateFormNationalities(names, lang = DEFAULT_LOCALE) {
  const existing = await adminFetch("form_nationalities", { params: { locale: lang } });
  const active = (existing || []).filter(r => r.active !== 0 && !r.deleted_at);
  for (let i = 0; i < names.length; i++) {
    const old = active[i];
    if (old) {
      if (old.name !== names[i] || parseInt(old.order_index) !== i) {
        await adminFetch("form_nationalities", { method: "PATCH", body: { uid: old.uid, name: names[i], order_index: i } });
      }
    } else {
      await adminFetch("form_nationalities", { method: "POST", body: { name: names[i], order_index: i, locale: lang } });
    }
  }
  for (let i = names.length; i < active.length; i++) {
    await adminFetch("form_nationalities", { method: "PATCH", body: { uid: active[i].uid, active: 0, deleted_at: new Date().toISOString() } });
  }
}

// ---- UI STRINGS ----
let _currentUserUid = null;
export function setCurrentUserUid(uid) { _currentUserUid = uid; }

let _uiStringsPromise = {};
let _uiStringsRawPromise = {};

export async function getUiStrings(lang = DEFAULT_LOCALE) {
  if (_uiStringsPromise[lang]) return _uiStringsPromise[lang];
  _uiStringsPromise[lang] = adminFetch("ui_strings", { params: { locale: lang } }).then(rows => {
    if (!_uiStringsRawPromise[lang]) _uiStringsRawPromise[lang] = rows || [];
    const grouped = {};
    (rows || []).forEach(row => {
      if (!grouped[row.context]) grouped[row.context] = {};
      grouped[row.context][row.key] = row.value;
    });
    return grouped;
  });
  return _uiStringsPromise[lang];
}

export async function updateUiStrings(context, values, lang = DEFAULT_LOCALE) {
  const raw = _uiStringsRawPromise[lang];
  if (!raw) {
    const allOps = Object.entries(values).map(([key, value]) =>
      adminFetch("ui_strings", { method: "POST", body: { context, key, value, locale: lang } })
    );
    if (allOps.length === 0) return;
    const results = await Promise.allSettled(allOps);
    if (results.some(r => r.status === "rejected")) throw new Error("Erro ao salvar textos");
    return;
  }
  const existing = {};
  raw.filter(r => r.context === context).forEach(r => { existing[r.key] = r; });

  const patchOps = [];
  const postOps = [];
  for (const [key, value] of Object.entries(values)) {
    if (existing[key]) {
      if (existing[key].value !== value) {
        const patchBody = { uid: existing[key].uid, value };
        if (_currentUserUid) patchBody.edited_by_uid = _currentUserUid;
        patchOps.push(adminFetch("ui_strings", { method: "PATCH", body: patchBody }));
        existing[key].value = value;
      }
    } else {
      const postBody = { context, key, value, locale: lang };
      if (_currentUserUid) postBody.created_by_uid = _currentUserUid;
      postOps.push(adminFetch("ui_strings", { method: "POST", body: postBody }).then(created => {
        if (created?.uid) raw.push({ uid: created.uid, context, key, value, locale: lang });
      }));
    }
  }
  if (patchOps.length === 0 && postOps.length === 0) return;
  const allOps = [...patchOps, ...postOps];
  const results = await Promise.allSettled(allOps);
  if (results.some(r => r.status === "rejected")) { _uiStringsRawPromise[lang] = null; throw new Error("Erro ao salvar textos"); }
  _uiStringsPromise[lang] = null;
}

// ---- PROFILE ----
export async function getProfile(userId) {
  try {
    const res = await fetch("/api/profile?" + new URLSearchParams({ user_uid: userId }).toString());
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function updateProfile(userId, updates) {
  const res = await fetch("/api/profile", {
    method: "PATCH",
    headers: adminHeaders,
    body: JSON.stringify({ user_uid: userId, ...updates }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Falha ao atualizar perfil");
  }
  return res.json();
}

export async function updateUser(userId, updates) {
  const rows = await adminFetch("users");
  const existing = (rows || []).find(r => String(r.uid) === String(userId));
  if (!existing) throw new Error("Usuário não encontrado");
  return adminFetch("users", { method: "PATCH", body: { uid: userId, ...updates } });
}

// ---- BLOG POSTS ----
export async function getBlogPosts(lang = DEFAULT_LOCALE) {
  const rows = await adminFetch("blog_posts", { params: { locale: lang, order: "created_at DESC" } });
  return (rows || []).filter(p => p.active !== 0 && !p.deleted_at).map(p => ({
    uid: p.uid,
    title: p.title || "",
    slug: p.slug || "",
    excerpt: p.excerpt || "",
    content: p.content || "",
    category: p.category || "",
    image_url: p.image_url || "",
    author_name: p.author_name || "",
    author_avatar: p.author_avatar || "",
    published: p.published === 1 || p.published === true ? 1 : 0,
    created_at: p.created_at,
    updated_at: p.updated_at,
  }));
}

export async function getBlogPostBySlug(slug, lang = DEFAULT_LOCALE) {
  const rows = await adminFetch("blog_posts", { params: { locale: lang } });
  const post = (rows || []).find(p => p.slug === slug && p.published === 1 && p.active !== 0 && !p.deleted_at);
  if (!post) return null;
  return {
    uid: post.uid,
    title: post.title || "",
    slug: post.slug || "",
    excerpt: post.excerpt || "",
    content: post.content || "",
    category: post.category || "",
    image_url: post.image_url || "",
    author_name: post.author_name || "",
    author_avatar: post.author_avatar || "",
    published: 1,
    created_at: post.created_at,
    updated_at: post.updated_at,
  };
}

export async function createBlogPost(form, lang = DEFAULT_LOCALE) {
  return adminFetch("blog_posts", {
    method: "POST",
    body: {
      ...form,
      locale: lang,
      published: form.published ? 1 : 0,
    },
  });
}

export async function updateBlogPost(uid, updates, lang = DEFAULT_LOCALE) {
  const payload = { uid, ...updates, locale: lang };
  if (payload.published !== undefined) payload.published = payload.published ? 1 : 0;
  return adminFetch("blog_posts", { method: "PATCH", body: payload });
}

export async function deleteBlogPost(uid) {
  return adminFetch("blog_posts", { method: "PATCH", body: { uid, active: 0, deleted_at: new Date().toISOString() } });
}

export async function getBlogTexts(lang = DEFAULT_LOCALE) {
  const ui = await getUiStrings(lang);
  const b = ui?.blog_section || {};
  return {
    eyebrow: b.eyebrow || "",
    headline: b.headline || "",
    description: b.description || "",
    read_more: b.read_more || "",
  };
}

export async function updateBlogTexts(values, lang = DEFAULT_LOCALE) {
  await updateUiStrings("blog_section", values, lang);
}

export async function getFAQs(lang = DEFAULT_LOCALE) {
  return adminFetch("faqs", { params: { locale: lang, order: "sort_order ASC" } });
}

export async function createFAQ(form, lang = DEFAULT_LOCALE) {
  return adminFetch("faqs", { method: "POST", body: { ...form, locale: lang } });
}

export async function updateFAQ(uid, updates, lang = DEFAULT_LOCALE) {
  return adminFetch("faqs", { method: "PATCH", body: { uid, ...updates, locale: lang } });
}

export async function deleteFAQ(uid) {
  return adminFetch("faqs", { method: "DELETE", params: { uid } });
}

// ---- FAQ Questions ----
export async function createFaqQuestion({ name, email, question }) {
  const res = await fetch("/api/faq-question", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, question }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao enviar pergunta");
  }
  return res.json();
}

export async function getFaqQuestions() {
  return adminFetch("faq_questions", { params: { order: "created_at DESC" } });
}

export async function updateFaqQuestion(uid, updates) {
  return adminFetch("faq_questions", { method: "PATCH", body: { uid, ...updates } });
}

export async function deleteFaqQuestion(uid) {
  return adminFetch("faq_questions", { method: "DELETE", params: { uid } });
}

export async function answerFaqQuestion({ id, answer }) {
  const res = await fetch("/api/faq-answer", {
    method: "POST",
    headers: adminHeaders,
    body: JSON.stringify({ uid: id, answer }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao responder pergunta");
  }
  return res.json();
}

// ---- FAQ CATEGORIES ----
export async function getFaqCategories(lang = DEFAULT_LOCALE) {
  return adminFetch("faq_categories", { params: { locale: lang, order: "name ASC" } });
}
export async function createFaqCategory(body, lang = DEFAULT_LOCALE) {
  return adminFetch("faq_categories", { method: "POST", body: { ...body, locale: lang } });
}
export async function updateFaqCategory(body) {
  return adminFetch("faq_categories", { method: "PATCH", body });
}

// ---- USERS ----
export async function getUsers() {
  return adminFetch("users", { params: { order: "created_at DESC" } });
}
export async function createUser(body) {
  return adminFetch("users", { method: "POST", body });
}
export async function deleteUser(uid) {
  return adminFetch("users", { method: "DELETE", params: { uid } });
}

// ---- ROLES ----
export async function getRoles() {
  return adminFetch("roles", { params: { order: "created_at DESC" } });
}
export async function createRole(body) {
  return adminFetch("roles", { method: "POST", body });
}
export async function updateRole(body) {
  return adminFetch("roles", { method: "PATCH", body });
}
export async function deleteRole(uid) {
  return adminFetch("roles", { method: "DELETE", params: { uid } });
}

export async function getEmailLogs(status) {
  let url = "/api/email-logs";
  if (status && status !== "all") {
    url += "?" + new URLSearchParams({ status }).toString();
  }
  const res = await fetch(url, { headers: adminHeaders });
  if (!res.ok) throw new Error("Erro ao carregar logs");
  return res.json();
}

export async function changePassword(userId, currentPassword, newPassword) {
  const res = await fetch("/api/change-password", {
    method: "POST",
    headers: adminHeaders,
    body: JSON.stringify({ userId, currentPassword, newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Falha ao alterar senha");
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function uploadAvatar(file) {
  const base64 = await fileToBase64(file);
  const res = await fetch("/api/upload/avatar", {
    method: "POST",
    headers: adminHeaders,
    body: JSON.stringify({ file: base64 }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Falha ao enviar avatar");
  }
  const data = await res.json();
  return data.url;
}

export async function uploadSiteImage(file) {
  const base64 = await fileToBase64(file);
  const res = await fetch("/api/upload/site-image", {
    method: "POST",
    headers: adminHeaders,
    body: JSON.stringify({ file: base64 }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Falha ao enviar imagem");
  }
  const data = await res.json();
  return data.url;
}

let _localesPromise = null;

export async function checkLocale(lang, source) {
  const body = { locale: lang };
  if (source) body.source = source;
  const res = await fetch("/api/check-locale", {
    method: "POST", headers: { "Content-Type": "application/json", "X-Admin-Token": ADMIN_TOKEN },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to check locale");
  return res.json();
}

export async function getLocales() {
  if (_localesPromise) return _localesPromise;
  _localesPromise = adminFetch("locales", { params: { order: "sort_order ASC" } });
  return _localesPromise;
}

export function clearLocalesCache() {
  _localesPromise = null;
  _uidCache = {};
  _uiStringsPromise = {};
  _uiStringsRawPromise = {};
  _settingsUidCache = {};
}
